import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { FileOutput, Loader2, Download, RefreshCw, Search, FileSpreadsheet, Crosshair, TrendingUp, X, Mail, Send, Wallet, ShieldCheck, AlertTriangle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, ResponsiveContainer, LabelList } from "recharts";

const fmtMonto = (v) => (v == null ? "-" : Number(v).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtFecha = (s) => (s ? String(s).slice(0, 10) : "-");
const DIAS_SEMANA = [
  { v: 0, l: "Lun" }, { v: 1, l: "Mar" }, { v: 2, l: "Mié" }, { v: 3, l: "Jue" },
  { v: 4, l: "Vie" }, { v: 5, l: "Sáb" }, { v: 6, l: "Dom" },
];

export default function LionbridgeEmisionesPage() {
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador"].includes((user?.role || "").toLowerCase());
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [ultimaCaptura, setUltimaCaptura] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sincronizando, setSincronizando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState({ destinatarios: [], hora: "07:00", dias: [0, 1, 2, 3, 4], activo: false });
  const [nuevoCorreo, setNuevoCorreo] = useState("");
  const [savingConfig, setSavingConfig] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [actualizando, setActualizando] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [loadError, setLoadError] = useState(null);

  const fetchHistorial = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/lionbridge/emisiones/config-envio/historial`, getAuthHeaders());
      setHistorial(res.data.items || []);
    } catch (e) { /* solo admin */ }
  }, [getAuthHeaders]);

  const fetchItems = useCallback(async (q = "", desde = "", hasta = "") => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.busqueda = q;
      if (desde) params.fecha_desde = desde;
      if (hasta) params.fecha_hasta = hasta;
      const res = await axios.get(`${API}/lionbridge/emisiones/`, { ...getAuthHeaders(), params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setUltimaCaptura(res.data.ultima_captura);
      if (res.data.stats) setStats(res.data.stats);
      setLoadError(null);
    } catch (e) {
      // No mostrar "no hay emisiones" cuando en realidad falló la carga
      const status = e.response?.status;
      const msg = status === 401 || status === 403
        ? "Tu sesión expiró. Vuelve a iniciar sesión para ver las emisiones."
        : (e.response?.data?.detail || "No se pudieron cargar las emisiones. Verifica tu conexión e intenta de nuevo.");
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const sincronizar = useCallback(async (silencioso = false) => {
    setSincronizando(true);
    try {
      const res = await axios.post(`${API}/lionbridge/emisiones/sincronizar`, {}, getAuthHeaders());
      const d = res.data;
      if (!silencioso || d.nuevas > 0) {
        toast.success(`Captura ${d.rango}: ${d.nuevas} nueva${d.nuevas !== 1 ? "s" : ""} · ${d.total} en total`);
      }
      fetchItems(busqueda, fechaDesde, fechaHasta);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al capturar desde el portal");
      fetchItems(busqueda, fechaDesde, fechaHasta);
    } finally {
      setSincronizando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAuthHeaders, fetchItems]);

  useEffect(() => {
    // Al abrir: captura automática (solo agrega nuevas) y carga la grilla
    sincronizar(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchItems(busqueda, fechaDesde, fechaHasta), 400);
    return () => clearTimeout(t);
  }, [busqueda, fechaDesde, fechaHasta, fetchItems]);

  const exportarExcel = async () => {
    setExportando(true);
    try {
      const params = {};
      if (busqueda) params.busqueda = busqueda;
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      const res = await axios.get(`${API}/lionbridge/emisiones/export/excel`, { ...getAuthHeaders(), params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lionbridge_emisiones_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch (e) {
      toast.error("Error al exportar");
    } finally {
      setExportando(false);
    }
  };

  const actualizarData = async () => {
    setActualizando(true);
    try {
      const res = await axios.post(`${API}/lionbridge/emisiones/actualizar`, {}, getAuthHeaders());
      const d = res.data;
      const errores = (d.dias_con_error || []).length;
      toast.success(
        `Data actualizada ${d.rango}: ${d.nuevas} nueva${d.nuevas !== 1 ? "s" : ""} · ${d.total} en total` +
        (errores ? ` · ${errores} día(s) con error en el portal` : ""),
        { duration: 6000 }
      );
      fetchItems(busqueda, fechaDesde, fechaHasta);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar la data");
    } finally {
      setActualizando(false);
    }
  };

  const openConfig = async () => {
    try {
      const res = await axios.get(`${API}/lionbridge/emisiones/config-envio`, getAuthHeaders());
      setConfig({
        destinatarios: res.data.destinatarios || [],
        hora: res.data.hora || "07:00",
        dias: res.data.dias || [0, 1, 2, 3, 4],
        activo: !!res.data.activo,
      });
    } catch (e) { /* solo admin */ }
    fetchHistorial();
    setShowConfig(true);
  };

  const addCorreo = () => {
    if (!nuevoCorreo || !nuevoCorreo.includes("@")) { toast.error("Correo no válido"); return; }
    if (config.destinatarios.includes(nuevoCorreo)) return;
    setConfig({ ...config, destinatarios: [...config.destinatarios, nuevoCorreo] });
    setNuevoCorreo("");
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.put(`${API}/lionbridge/emisiones/config-envio`, config, getAuthHeaders());
      toast.success("Configuración guardada");
      setShowConfig(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSavingConfig(false); }
  };

  const sendNow = async () => {
    setSendingNow(true);
    try {
      const res = await axios.post(`${API}/lionbridge/emisiones/config-envio/enviar-ahora`, {}, getAuthHeaders());
      toast.success(res.data.message || "Enviado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar");
    } finally {
      setSendingNow(false);
      fetchHistorial();
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="lionbridge-emisiones-page">
        <div className="flex flex-wrap justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-bold dark:text-white flex items-center gap-2">
              <FileOutput className="w-7 h-7 text-[#0C314C]" /> Emisiones
            </h1>
            <p className="text-slate-500">
              Proyecto LionBridge — pólizas emitidas capturadas del portal One Alliance
              {ultimaCaptura && <span className="ml-2 text-xs">(última captura: {new Date(ultimaCaptura).toLocaleString("es-DO")})</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button onClick={actualizarData} disabled={actualizando}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" data-testid="emisiones-actualizar-btn">
                {actualizando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Actualizar Data
              </Button>
            )}
            {isAdmin && (
              <Button variant="outline" onClick={openConfig} data-testid="emisiones-envio-diario-btn">
                <Mail className="w-4 h-4 mr-2" />Envío Diario
              </Button>
            )}
            <Button onClick={() => sincronizar(false)} disabled={sincronizando} data-testid="emisiones-sincronizar-btn">
              {sincronizando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Capturar ahora
            </Button>
            <Button variant="outline" onClick={exportarExcel} disabled={exportando || items.length === 0} data-testid="emisiones-export-btn">
              {exportando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
              Exportar Excel
            </Button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-[#0C314C] to-[#2d5382] text-white" data-testid="card-prima-total">
              <CardContent className="p-4 h-full flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-slate-200">Prima Total Emitida</p>
                  <p className="text-3xl font-bold my-1 whitespace-nowrap">RD$ {fmtMonto(stats.prima_total)}</p>
                  <p className="text-xs text-slate-300" data-testid="prima-hoy">Hoy: RD$ {fmtMonto(stats.prima_hoy)}</p>
                </div>
                <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                  <Wallet className="w-7 h-7 text-white/80" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white" data-testid="card-polizas-total">
              <CardContent className="p-4 h-full flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-emerald-100">Pólizas Emitidas</p>
                  <p className="text-3xl font-bold my-1">{stats.polizas_total}</p>
                  <p className="text-xs text-emerald-200" data-testid="polizas-hoy">Hoy: {stats.polizas_hoy}</p>
                </div>
                <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-7 h-7 text-white/80" />
                </div>
              </CardContent>
            </Card>
            <Card data-testid="card-marcas" className="overflow-hidden border-slate-200 dark:border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
                    <Crosshair className="w-4 h-4 text-[#0C314C] dark:text-slate-300" /> Marcas de Armas Aseguradas
                  </p>
                  <Badge variant="secondary" className="text-[10px] font-semibold" data-testid="marcas-total-badge">
                    {(stats.marcas || []).length} marcas
                  </Badge>
                </div>
                <div className="space-y-1.5 max-h-[58px] overflow-y-auto pr-1">
                  {(stats.marcas || []).map((m, i) => {
                    const max = stats.marcas[0]?.cantidad || 1;
                    return (
                      <div key={m.marca} className="flex items-center gap-2" data-testid={`marca-${m.marca}`}>
                        <span className="w-4 shrink-0 text-[10px] font-mono text-slate-400 text-right">{i + 1}</span>
                        <span className="w-24 shrink-0 text-xs font-medium text-slate-700 dark:text-slate-200 truncate" title={m.marca}>{m.marca}</span>
                        <div className="flex-1 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-[#0C314C] to-[#4a7ab5] transition-[width] duration-500"
                            style={{ width: `${Math.max((m.cantidad / max) * 100, 4)}%` }}
                          />
                        </div>
                        <span className="w-7 shrink-0 text-xs font-bold text-[#0C314C] dark:text-slate-200 text-right">{m.cantidad}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {stats && (stats.semanas || []).length > 0 && (
          <Card className="mb-6" data-testid="card-tendencia">
            <CardContent className="p-4">
              <p className="text-sm font-medium text-slate-600 dark:text-slate-300 flex items-center gap-1.5 mb-3">
                <TrendingUp className="w-4 h-4 text-[#0C314C] dark:text-slate-300" /> Prima Emitida por Semana
              </p>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats.semanas} margin={{ top: 22, right: 60, left: 30, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="etiqueta" tick={{ fontSize: 11, fill: "#64748b" }} padding={{ left: 45, right: 45 }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} width={45} />
                    <RTooltip
                      formatter={(v, name) => name === "prima" ? [`RD$ ${fmtMonto(v)}`, "Prima"] : [v, "Pólizas"]}
                      labelFormatter={(l, p) => `Semana del ${p?.[0]?.payload?.semana || l}`}
                    />
                    <Line type="monotone" dataKey="prima" stroke="#0C314C" strokeWidth={2.5}
                      dot={{ r: 3, fill: "#0C314C" }} activeDot={{ r: 5 }}>
                      <LabelList dataKey="prima" position="top" offset={10}
                        formatter={(v) => `RD$ ${fmtMonto(v)}`}
                        style={{ fontSize: 10, fontWeight: 600, fill: "#0C314C" }} />
                    </Line>
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Buscar por cliente, cédula, NCF, póliza, factura, marca o serial..."
                  className="pl-9"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  data-testid="emisiones-busqueda-input"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-500 whitespace-nowrap">Desde</span>
                <Input type="date" className="w-[150px] text-xs" value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)} data-testid="emisiones-fecha-desde" />
                <span className="text-xs text-slate-500 whitespace-nowrap">Hasta</span>
                <Input type="date" className="w-[150px] text-xs" value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)} data-testid="emisiones-fecha-hasta" />
                {(fechaDesde || fechaHasta) && (
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500"
                    onClick={() => { setFechaDesde(""); setFechaHasta(""); }} data-testid="emisiones-limpiar-fechas">
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <Badge variant="outline" data-testid="emisiones-total-badge">{total} emisiones</Badge>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : loadError ? (
              <div className="text-center py-16 text-slate-500" data-testid="emisiones-load-error">
                <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-red-500" />
                <p className="font-semibold text-red-600 mb-3">{loadError}</p>
                <Button variant="outline" onClick={() => fetchItems(busqueda, fechaDesde, fechaHasta)} data-testid="emisiones-reintentar-btn">
                  Reintentar
                </Button>
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <Download className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No hay emisiones capturadas todavía. Pulsa "Capturar ahora".</p>
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[65vh] overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700">
                <table className="w-full text-xs" data-testid="emisiones-tabla">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-[#0C314C] text-white">
                      {["Identificación", "Cliente", "Póliza", "Inicio Vig.", "Fin Vig.", "Factura", "Monto", "NCF", "Recibo", "Monto Recibo", "Fecha Recibo", "Marca Arma", "Serial/Modelo", "Coberturas"].map(h => (
                        <th key={h} className="px-2 py-2 text-left whitespace-nowrap font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((r, i) => (
                      <tr key={r.clave_unica} className={`border-b border-slate-100 dark:border-slate-800 hover:bg-blue-50/60 dark:hover:bg-slate-800/60 ${i % 2 === 1 ? "bg-slate-50/70 dark:bg-slate-800/30" : ""}`} data-testid={`emision-row-${r.clave_unica}`}>
                        <td className="px-2 py-1.5 whitespace-nowrap">{r.identificacion_cliente}</td>
                        <td className="px-2 py-1.5 font-medium whitespace-nowrap">{r.nombre_cliente}</td>
                        <td className="px-2 py-1.5">{r.numero_poliza}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{fmtFecha(r.inicio_vigencia)}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{fmtFecha(r.fin_vigencia)}</td>
                        <td className="px-2 py-1.5">{r.nro_factura}</td>
                        <td className="px-2 py-1.5 text-right whitespace-nowrap">{fmtMonto(r.monto_factura)}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{r.ncf}</td>
                        <td className="px-2 py-1.5">{r.nro_recibo_cobro}</td>
                        <td className="px-2 py-1.5 text-right whitespace-nowrap" data-testid={`monto-recibo-${r.clave_unica}`}>{r.monto_recibo != null ? fmtMonto(r.monto_recibo) : "—"}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{fmtFecha(r.fecha_recibo_cobro)}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{r.marca_arma}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{r.serial_arma}</td>
                        <td className="px-2 py-1.5 max-w-[280px] truncate" title={(r.coberturas || []).join("; ")}>{(r.coberturas || []).join("; ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialog Envío Diario del resumen de Emisiones */}
        <Dialog open={showConfig} onOpenChange={setShowConfig}>
          <DialogContent className="max-w-lg" data-testid="emisiones-config-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5" />Envío Diario del Resumen de Emisiones</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-slate-500 -mt-2">
              Cada mañana se envía el resumen (prima y pólizas) del día anterior con el Excel adjunto.
              Los lunes se envían las transacciones del viernes anterior.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input type="checkbox" id="emisiones-activo" checked={config.activo}
                  onChange={(e) => setConfig({ ...config, activo: e.target.checked })} data-testid="emisiones-config-activo" />
                <Label htmlFor="emisiones-activo">Activar envío automático diario</Label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Hora (RD)</Label>
                  <Input type="time" value={config.hora} onChange={(e) => setConfig({ ...config, hora: e.target.value })} data-testid="emisiones-config-hora" />
                </div>
              </div>
              <div>
                <Label>Días de envío</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {DIAS_SEMANA.map((d) => {
                    const active = config.dias.includes(d.v);
                    return (
                      <button key={d.v} type="button"
                        onClick={() => setConfig({ ...config, dias: active ? config.dias.filter((x) => x !== d.v) : [...config.dias, d.v] })}
                        className={`px-3 py-1 rounded-full text-sm border ${active ? "bg-[#0C314C] text-white border-[#0C314C]" : "border-slate-300 text-slate-600"}`}
                        data-testid={`emisiones-config-dia-${d.v}`}>{d.l}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Destinatarios</Label>
                <div className="flex gap-2 mt-1">
                  <Input placeholder="correo@empresa.com" value={nuevoCorreo} onChange={(e) => setNuevoCorreo(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addCorreo()} data-testid="emisiones-config-nuevo-correo" />
                  <Button type="button" variant="outline" onClick={addCorreo} data-testid="emisiones-config-agregar-correo">Agregar</Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {config.destinatarios.map((c) => (
                    <span key={c} className="inline-flex items-center gap-1 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-xs" data-testid={`emisiones-destinatario-${c}`}>
                      {c}<button onClick={() => setConfig({ ...config, destinatarios: config.destinatarios.filter((x) => x !== c) })}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {config.destinatarios.length === 0 && <span className="text-xs text-slate-400">Sin destinatarios</span>}
                </div>
              </div>
              <div>
                <Label>Últimos envíos</Label>
                <div className="mt-1 space-y-1 max-h-36 overflow-y-auto pr-1" data-testid="emisiones-historial-envios">
                  {historial.length === 0 ? (
                    <p className="text-xs text-slate-400">Sin envíos registrados todavía</p>
                  ) : historial.map((h, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs bg-slate-50 dark:bg-slate-800 rounded px-2 py-1.5" data-testid={`emisiones-envio-log-${i}`}>
                      <div className="min-w-0 truncate">
                        <span className="font-medium">{h.sent_at ? new Date(h.sent_at).toLocaleString("es-DO", { dateStyle: "short", timeStyle: "short" }) : "-"}</span>
                        <span className="text-slate-400 ml-1">· resumen del {h.fecha_reportada}</span>
                        <span className="text-slate-400 ml-1">· {h.tipo === "manual" ? "manual" : "automático"}</span>
                        <span className="text-slate-400 ml-1">· {h.polizas} póliza(s)</span>
                      </div>
                      <span title={h.error || ""}
                        className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${h.ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {h.ok ? "Enviado" : "Falló"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={sendNow} disabled={sendingNow || config.destinatarios.length === 0} data-testid="emisiones-enviar-ahora-btn">
                {sendingNow ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Enviar resumen de ayer ahora
              </Button>
              <Button onClick={saveConfig} disabled={savingConfig} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" data-testid="emisiones-save-config-btn">
                {savingConfig ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Guardar Configuración
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
